import { createContext, useContext, useState, useEffect, useMemo } from "react";

const CartContext = createContext();

const SYMBOLS = {
  PKR: "₨",
  USD: "$",
  EUR: "€",
  GBP: "£",
  AUD: "A$",
  SAR: "﷼",
};

export function CartProvider({ children }) {
  // ✅ Load cart from localStorage so a refresh doesn't empty it
  const [cartItems, setCartItems] = useState(() => {
    try {
      const saved = localStorage.getItem("cartItems");
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [currency, setCurrency] = useState("PKR");

  // ✅ Persist cart on every change
  useEffect(() => {
    try {
      localStorage.setItem("cartItems", JSON.stringify(cartItems));
    } catch {
      /* ignore quota / private-mode errors */
    }
  }, [cartItems]);

  useEffect(() => {
    const saved = localStorage.getItem("currency");
    if (saved) {
      setCurrency(saved);
      return;
    }

    fetch("https://ipwho.is/")
      .then((res) => res.json())
      .then((data) => {
        const cc = data.country_code;
        let chosen = "USD";
        if (cc === "PK") chosen = "PKR";
        else if (cc === "US") chosen = "USD";
        else if (cc === "GB") chosen = "GBP";
        else if (cc === "AU") chosen = "AUD";
        else if (cc === "SA") chosen = "SAR";
        else if (["DE", "FR", "IT", "ES", "NL"].includes(cc)) chosen = "EUR";
        localStorage.setItem("currency", chosen);
        setCurrency(chosen);
      })
      .catch(() => {
        setCurrency("PKR");
      });
  }, []);

  // persist currency choice
  useEffect(() => {
    localStorage.setItem("currency", currency);
  }, [currency]);

  // ✅ Get fixed price from item.prices[currency]
  const getUnitPrice = (item) => Number(item?.prices?.[currency]) || 0;

  const addToCart = (product) => {
    // product MUST include: { id, title, prices: {PKR,USD,...}, image? }
    if (!product?.id) return;

    if (!product?.prices || typeof product.prices !== "object") {
      console.warn("Cart item missing 'prices' map:", product);
    }

    setCartItems((prev) => {
      const existing = prev.find((item) => item.id === product.id);
      const qtyAdd = Math.max(1, Number(product.quantity) || 1);

      if (existing) {
        return prev.map((item) =>
          item.id === product.id
            ? { ...item, quantity: (Number(item.quantity) || 1) + qtyAdd }
            : item
        );
      }

      return [
        ...prev,
        {
          id: product.id,
          title: product.title || "",
          image: product.image || "",
          prices: product.prices || {},   // ✅ key part
          quantity: qtyAdd,
        },
      ];
    });
  };

  const removeFromCart = (id) =>
    setCartItems((prev) => prev.filter((item) => item.id !== id));

  const clearCart = () => setCartItems([]);

  // ✅ Total in selected currency (no conversion)
  const totalPrice = useMemo(() => {
    return cartItems.reduce((acc, item) => {
      const unit = Number(item?.prices?.[currency]) || 0;
      const qty = Number(item.quantity) || 1;
      return acc + unit * qty;
    }, 0);
  }, [cartItems, currency]);

  const currencySymbol = SYMBOLS[currency] || "$";

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        clearCart,
        currency,
        setCurrency,
        currencySymbol,
        getUnitPrice, // ✅ useful in Checkout + Navbar
        totalPrice,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
